import { Review, User } from "@/types";
import { ServerCollectionResponse, Service } from "@/types/businessTypes";
import { Currency } from "@/lib/utils/currency";

export type OrderCollection = ServerCollectionResponse<Order>;

export type Order = {
	id: string;
	orderStatus: OrderStatus;
	items: OrderItem[];
	createdAt: string;
	invoiceUrl: string;
	userId: User;
	files: string[];
	amountTotal?: number;
	submissions: string[];
	currency?: Currency;
	paymentOption?: "full" | "installments" | "trial";
	installmentCount?: number;
	firstInstallmentAmount?: number;
	/** Stripe billing_cycle_anchor for the first remaining installment charge. */
	installmentNextPaymentDueAt?: string | null;
	servicePageCheckout?: Record<string, unknown>;
	basketCheckoutStripe?: Record<string, unknown>;
	/** Present after Stripe checkout — used with client `purchase` to match MP `transaction_id` */
	stripeSessionId?: string;
	/** Post-checkout domain step: pending shows setup UI; submitted/deferred shows confirmation. */
	domainSetupStatus?: "pending" | "submitted" | "deferred";
	customerDomain?: string;
};

export interface OrderCreateAddon {
	addonId: string;
	quantity: number;
}

export interface OrderCreate {
	items: {
		serviceId: string;
		preferredDesignServiceId?: string;
		package: { packageId: string; quantity: number };
		addons: OrderCreateAddon[];
	}[];
	paymentOption?: 'full' | 'installments' | 'trial';
	installmentCount?: number;
	/** Due-today / advance for installments (same rule as package card: non-package lines + 10% of package subtotal, capped to total; backend may fall back to 97 × line count). */
	firstInstallmentAmount?: number;
	currency?: Currency;
	theme?: 'light' | 'dark';
	/** Service page: hosting pill + member discount — backend recomputes totals */
	servicePageCheckout?: {
		hostingPlanId?: string;
		hostingBillingCycle?: "monthly" | "yearly";
		applyMemberDiscount: boolean;
	};
	/** Basket: one hosting plan when every line’s category has hosting enabled (server requires it). */
	basketHostingCheckout?: {
		hostingPlanId: string;
		hostingBillingCycle: "monthly" | "yearly";
	};
	/** Storefront promo codes — validated again on the server. */
	promoCode?: string;
	promoCodes?: string[];
	/** Unauthenticated Buy Now — Stripe collects email; account created after payment. */
	guestCheckout?: boolean;
}

export interface OrderItem {
	_id: string;
	subTotal?: number;
	displayOrderId?: string;
	// Could be null when the service has been deleted
	serviceId: (Omit<Service, "parentServiceId"> & { parentServiceId?: string }) | null;
	preferredDesignServiceId?: (Pick<Service, "_id" | "id" | "title" | "slug" | "thumbnailImage"> & {
		_id?: string;
	}) | string | null;
	preferredDesignTitle?: string;
	reviewId: Review | null;
	package: {
		packageId: string;
		quantity: number;
	};
	progress: OrderProgress;
	noLongerNeedsHosting?: boolean;
	addons: OrderCreateAddon[];
	createdAt: string;
	updatedAt: string;
}

export enum OrderProgress {
	New = "NEW",
	InProgress = "IN_PROGRESS",
	InReview = "IN_REVIEW",
	Completed = "COMPLETED",
	Cancelled = "CANCELLED",
	Late = "LATE",
}

export enum OrderStatus {
	UNPAID = "UNPAID",
	PAID = "PAID",
}

export type OrderAggregated = {
	count: number;
	progress: OrderProgress;
};
